import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useFacilityProcessFilterOptions } from '@/components/shared/FacilityProcessOptions/hooks/useFacilityProcessFilterOptions';
import { PageContainer } from '@/components/shared/PageContainer';
import { useRelationshipTypeOptions } from '@/hooks/useDropdowns';
import { useFacilityProcesses } from '@/hooks/useFacilityProcesses';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { CountryDetailsSheet } from '../../../-components/CountryDetailsSheet';
import { DetailedRiskLandscape } from '../../../-components/DetailedRiskLandscape';
import { GlobalRiskLandscape } from '../../../-components/GlobalRiskLandscape';
import { useAllRisks } from '../../../-hooks/useAllRisks';
import { useCountryRiskDetail } from '../../../-hooks/useCountryRiskDetail';
import { useGlobalRiskLandscape } from '../../../-hooks/useGlobalRiskLandscape';
import { useRiskCompaniesTable } from '../../../-hooks/useRiskCompaniesTable';
import { useRiskCountriesTable } from '../../../-hooks/useRiskCountriesTable';
import { useRiskLandscapeMap } from '../../../-hooks/useRiskLandscapeMap';

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(list)/landscape/',
)({
  component: RiskLandscapePage,
});

function RiskLandscapePage() {
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null,
  );

  const global = useGlobalRiskLandscape();
  const mapQuery = useRiskLandscapeMap(global.filters, global.scope);
  const countryDetail = useCountryRiskDetail(selectedCountryCode);
  const allRisks = useAllRisks();
  const countriesTable = useRiskCountriesTable();
  const companiesTable = useRiskCompaniesTable();
  const { resolveLabel: resolveFacilityProcess } = useFacilityProcesses();
  const facilityProcessOptions = useFacilityProcessFilterOptions();
  const { data: relationshipTypeOptions = [] } = useRelationshipTypeOptions();
  const countryOptions = COUNTRY_OPTIONS;
  const resolveRelationshipType = (id: string) =>
    relationshipTypeOptions.find(r => r.key === id)?.label ?? id;

  const handleCountryClick = (countryCode: string) =>
    setSelectedCountryCode(countryCode);

  const handleSheetClose = (open: boolean) => {
    if (!open) setSelectedCountryCode(null);
  };

  return (
    <PageContainer className='!overflow-y-auto gap-8'>
      <GlobalRiskLandscape
        rows={global.rows}
        total={global.total}
        mapData={mapQuery.data}
        isLoading={global.isLoading}
        scope={global.scope}
        sorting={global.sorting}
        filters={global.filters}
        pagination={global.pagination}
        onScopeChange={global.onScopeChange}
        onSortingChange={global.onSortingChange}
        onFiltersChange={global.onFiltersChange}
        onPaginationChange={global.onPaginationChange}
        onCountryClick={handleCountryClick}
        countryOptions={countryOptions}
      />

      <DetailedRiskLandscape
        allRisks={allRisks.data}
        countries={{
          rows: countriesTable.rows,
          total: countriesTable.total,
          isLoading: countriesTable.isLoading,
          sorting: countriesTable.sorting,
          filters: countriesTable.filters,
          pagination: countriesTable.pagination,
          onSortingChange: countriesTable.onSortingChange,
          onFiltersChange: countriesTable.onFiltersChange,
          onPaginationChange: countriesTable.onPaginationChange,
        }}
        companies={{
          rows: companiesTable.rows,
          total: companiesTable.total,
          isLoading: companiesTable.isLoading,
          sorting: companiesTable.sorting,
          filters: companiesTable.filters,
          pagination: companiesTable.pagination,
          onSortingChange: companiesTable.onSortingChange,
          onFiltersChange: companiesTable.onFiltersChange,
          onPaginationChange: companiesTable.onPaginationChange,
        }}
        onCountryClick={handleCountryClick}
        resolveFacilityProcess={resolveFacilityProcess}
        resolveRelationshipType={resolveRelationshipType}
        countryOptions={countryOptions}
        facilityProcessOptions={facilityProcessOptions}
        relationshipTypeOptions={relationshipTypeOptions}
      />

      <CountryDetailsSheet
        open={!!selectedCountryCode}
        onOpenChange={handleSheetClose}
        data={countryDetail.data}
        isLoading={countryDetail.isLoading}
      />
    </PageContainer>
  );
}
